import { useState } from "react";
import { ApiMessageHandler, ArtAlert, DynamicForm, useIdpService, validators, withFormService } from "@art/shared-ui";

interface RolePermissionAddPageProps {
  data?: { id: number; name: string; description: string } | null;
  onSave: () => void;
  onInit?: any;
  tenantName: string;
}

function RolePermissionAddPage({ data, onSave, onInit, tenantName }: RolePermissionAddPageProps) {
  const idpService = useIdpService();
  const [saveError, setSaveError] = useState("");

  const formBuilder = (formData: any) => {
    return [
      {
        type: "hidden",
        id: "id",
        value: data?.id,
      },
      {
        type: "row",
        children: [
          {
            type: "col",
            classNames: ["col-12"],
            children: [
              {
                id: "name",
                label: "Role Name",
                type: "text",
                placeholder: "Role Name",
                required: true,
                value: formData?.name || data?.name,
                dataQa: "role-name",
                validate: validators((value: string) => {
                  if (!value || value.trim() === "") {
                    return "Role Name is required.";
                  }
                  if (/[^a-zA-Z0-9 ]/.test(value)) {
                    return "Role Name can only contain letters, numbers, and spaces.";
                  }
                  if (!/[a-zA-Z]/.test(value)) {
                    return "Role Name must include at least one letter.";
                  }
                  if (value.length > 30) {
                    return "Role Name cannot be longer than 30 characters.";
                  }
                  return null;
                }),
              },
            ],
          },
        ],
      },
      {
        type: "row",
        children: [
          {
            type: "col",
            classNames: ["col-12"],
            children: [
              {
                id: "description",
                label: "Role Description",
                type: "text",
                placeholder: "Description",
                marginBottom: 0,
                required: true,
                multiline: true,
                rows: 3,
                value: formData?.description || data?.description,
                dataQa: "role-description",
                validate: validators((value: string) => {
                  if (!value || value.trim() === "") {
                    return "Role description is required.";
                  }
                  if (value.length > 100) {
                    return "Role description cannot be longer than 100 characters.";
                  }
                  return null;
                }),
              },
            ],
          },
        ],
      },
    ];
  };

  const sanitize = (formData: Record<string, any>) => ({
    name: (formData.name || "").trim(),
    description: (formData.description || "").trim(),
  });

  return (
    <>
      {saveError && <ArtAlert severity="error" sx={{ mb: 2 }}>{saveError}</ArtAlert>}
      <DynamicForm
        formBuilder={formBuilder}
        onInit={onInit}
        onSubmit={(formData: Record<string, any>) => {
          setSaveError("");
          const payload = sanitize(formData);

          if (!data) {
            return idpService.saveData(`api/tenants/${tenantName}/roles`, "post", {
              onSuccess: () => onSave(),
              onError: (err: unknown) => setSaveError(ApiMessageHandler.extractErrorMessage(err)),
            })(payload);
          }

          return idpService.saveData(`api/tenants/${tenantName}/roles/${data.id}`, "put", {
            onSuccess: () => onSave(),
            onError: (err: unknown) => setSaveError(ApiMessageHandler.extractErrorMessage(err)),
          })(payload);
        }}
      />
    </>
  );
}

export default withFormService(RolePermissionAddPage);
